import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useVault } from './VaultContext';
import { apiGet } from '../api/client';
import { AccountTier, VaultRole } from '../types/api';
import type { UnreadCountResponse } from '../types/api';
import VaultChatPanel from './VaultChatPanel';
import BugReportModal from './BugReportModal';
import EmailFamilyModal from './EmailFamilyModal';
import './Layout.css';
import './VaultChatPanel.css';

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { activeVaultId, activeVaultName, activeVaultAccountTier, activeVaultUserRole } = useVault();
  const [chatOpen, setChatOpen] = useState(false);
  const [emailFamilyOpen, setEmailFamilyOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bugReportOpen, setBugReportOpen] = useState(false);

  async function handleSignOut() {
    await logout();
    navigate('/login');
  }

  // Fetch unread count whenever active vault changes
  useEffect(() => {
    if (!activeVaultId) {
      setUnreadCount(0);
      setChatOpen(false);
      return;
    }

    apiGet<UnreadCountResponse>(`/api/vault/${activeVaultId}/chat/unread`)
      .then(data => setUnreadCount(data.count))
      .catch(() => {});
  }, [activeVaultId]);

  return (
    <>
      <nav className="layout-nav">
        <Link to="/" className="layout-brand">
          <svg className="layout-logo" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect width="32" height="32" rx="6" fill="#1a1a2e"/>
            <path d="M16 3 L5 7.5 L5 18 C5 24.5 10 29 16 31 C22 29 27 24.5 27 18 L27 7.5 Z" fill="#a0785a"/>
            <polygon points="16,9 11.5,14 13.5,14 10,19.5 13.5,19.5 13.5,23 18.5,23 18.5,19.5 22,19.5 18.5,14 20.5,14" fill="#f8f7f4"/>
          </svg>
          Cedar Vault<span className="layout-brand-tm">&#174;</span>
        </Link>
        <div className="layout-right">
          {user && (
            <span className="layout-greeting">
              Hi, {user.firstName}
            </span>
          )}
          {activeVaultId && (
            <>
              {user?.isAdmin && (
                <Link to="/admin" className="layout-admin-link">Admin</Link>
              )}
              <Link to="/change-password" className="btn-text layout-change-pw">Change Password</Link>
              {activeVaultAccountTier === AccountTier.Trial && (
                <span className="layout-badge layout-badge--trial">Free Trial</span>
              )}
              {activeVaultAccountTier === AccountTier.ForeverFree && (
                <span className="layout-badge layout-badge--forever-free">Forever Free</span>
              )}
            </>
          )}
          <button className="layout-signout" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </nav>
      <main className="layout-content">
        <Outlet />
      </main>
      <footer className="layout-footer">
        <Link to="/help">Help</Link>
        <span className="layout-footer-sep">&middot;</span>
        <a href="https://cedarvaultapp.com" target="_blank" rel="noopener noreferrer">
          cedarvaultapp.com
        </a>
        <span className="layout-footer-sep">&middot;</span>
        <a href="https://cedarvaultapp.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        <span className="layout-footer-sep">&middot;</span>
        <button className="layout-footer-report-btn" onClick={() => setBugReportOpen(true)} type="button">
          Report a Problem
        </button>
      </footer>

      {bugReportOpen && <BugReportModal onClose={() => setBugReportOpen(false)} />}

      {/* Floating action buttons — hidden for Advisors (read-only) */}
      {activeVaultId && activeVaultUserRole !== VaultRole.Advisor && (
        <>
          {chatOpen && (
            <VaultChatPanel
              vaultId={activeVaultId}
              vaultName={activeVaultName ?? 'Vault Chat'}
              onClose={() => setChatOpen(false)}
              onUnreadChange={setUnreadCount}
            />
          )}
          {emailFamilyOpen && (
            <EmailFamilyModal
              vaultId={activeVaultId}
              vaultName={activeVaultName ?? 'your vault'}
              onClose={() => setEmailFamilyOpen(false)}
            />
          )}
          {/* Email Family button — fixed above the chat button */}
          <button
            className="chat-float-btn chat-float-btn--email"
            onClick={() => {
              setEmailFamilyOpen(prev => !prev);
              setChatOpen(false);
            }}
            aria-label={emailFamilyOpen ? 'Close email composer' : 'Email your family'}
            title="Email Family"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M2 7l10 7 10-7"/>
              </svg>
          </button>
          <div className="chat-float-wrapper">
            {/* Chat button */}
            <button
              className="chat-float-btn"
              onClick={() => {
                setChatOpen(prev => !prev);
                setEmailFamilyOpen(false);
              }}
              aria-label={chatOpen ? 'Close chat' : 'Open chat'}
              title="Vault Chat"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
            {unreadCount > 0 && (
              <span className="chat-unread-badge" aria-label={`${unreadCount} unread messages`}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
}
